// src/utils/imageUrl.js
// Single source of truth for resolving product/category image URLs.
//
// The backend now returns full https:// URLs, but this helper gracefully
// handles any leftover relative paths or double-prefixed URLs that might
// exist in older cached data.

const FRONTEND_PUBLIC_BASE = 'https://visioncareoptics.in/frontend/public';
const PLACEHOLDER = '/assets/product-placeholder.jpg';

/**
 * Resolve any image URL coming from the API into a usable <img src> value.
 *
 * Handles:
 *   - null / undefined / empty string  → placeholder
 *   - already-correct full https URL   → returned as-is
 *   - double-prefixed URL              → de-duped
 *   - bare relative path /assets/...   → prefixed with base URL
 *
 * @param {string|null|undefined} url
 * @param {string} [placeholder]
 * @returns {string}
 */
export function resolveImageUrl(url, placeholder = PLACEHOLDER) {
  if (!url) return placeholder;

  // Strip any accidental double-prefix first
  // e.g. "https://.../frontend/publichttps://.../frontend/public/assets/..."
  const cleaned = url.replace(
    new RegExp(`(${FRONTEND_PUBLIC_BASE})+`, 'g'),
    FRONTEND_PUBLIC_BASE
  );

  // Already a full URL
  if (cleaned.startsWith('http')) return cleaned;

  // Relative path — prepend base
  return FRONTEND_PUBLIC_BASE + (cleaned.startsWith('/') ? cleaned : '/' + cleaned);
}

/**
 * Pick the best image URL from a product's images array.
 * Returns the primary image if available, otherwise the first one.
 *
 * @param {Array<{image_url: string, is_primary?: number}>} images
 * @param {string} [placeholder]
 * @returns {string}
 */
export function getProductImageUrl(images, placeholder = PLACEHOLDER) {
  if (!images || images.length === 0) return placeholder;
  const primary = images.find(img => img.is_primary === 1 || img.is_primary === '1');
  const chosen = primary || images[0];
  return resolveImageUrl(chosen?.image_url, placeholder);
}